/**
 * Quote screen (§5.2/§6): journey restatement up top, itemized lines, the
 * 48px navy total, ONE gold primary (Accept), Override as secondary with the
 * required-note-for-computed parity, suggested-duplicate warn banner, and
 * the walk-in name gate (a nameless Accept is an honest price check).
 * Manual services show the priced-by-hand state; the full hand-quote
 * surface lands in M5.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import { FormField, errorIdFor } from '@ecmb/ui';
import type { JobState, QuoteLine, QuoteOptions } from '../store/state.js';
import { shownReturnLeg, type ParkOutcome, type PersistOutcome } from '../lib/actions.js';
import { isReturnDay, type ReturnLegCause } from '../../shared/return-leg.js';
import type { Registry } from '../store/registry.js';
import { euro, whenText } from '../lib/format.js';
import { Banner } from '../components/Banner.js';
import { TotalBar } from '../components/TotalBar.js';
import { CallInCapture, type WhenPatch } from '../components/CallInCapture.js';
import { CustomerInfoBlock } from '../components/CustomerInfoBlock.js';
import { VenueControl } from '../components/VenueControl.js';
import { DriverDetails, DriverDetailsLines, type DriverEndPatch } from '../components/DriverDetails.js';
import { Requirements } from '../components/Requirements.js';
import { BandControl } from '../components/BandControl.js';
import { TeamAlert, type TeamAlertProps } from '../components/TeamAlert.js';
import { displayRouteLabel, formatRouteLabel } from '../../shared/route-label.js';
import { summaryWithDraftName } from '../store/selectors.js';
// ⛔ The engine's own over-band token - imported, never re-spelled here. Since
// T1871 M6 check-copy.sh's `scan_over_band` arm walks all of src and fails on
// the literal outside engine/bands.ts, so a local copy is now caught rather
// than left to drift unwatched.
import { OVER_BAND } from '../../engine/bands.js';

/* ⛔ `CapturedPriceView` (the account's captured price on a nursing job) WAS HERE and
   went with the nursing tile at board 2814 M6 (DD-8): no nursing job reaches this
   screen. The agreed price now shows on the phone-in form (PhoneInTrip.tsx). */

/**
 * Where the caret goes when a failure is raised. 'banner' is the default and the
 * right answer for almost everything; 'name' is the walk-in / hard name gates,
 * which point at the field to fix (M3 ux-review) — see the `fail()` block below.
 */
type FailureTarget = 'banner' | 'name';
/** ⛔ AN OBJECT, NOT A STRING, AND THAT IS THE ENFORCEMENT MECHANISM — see the
 *  `fail()` block in QuoteScreen. A future branch cannot set a bare string. */
type Failure = { text: string; target: FailureTarget };

export type QuoteScreenProps = {
  job: JobState;
  summary: string;
  isManual: boolean;
  registry: Registry;
  /** Registry labels for the customer's original asks (info block). */
  customerExtrasLabels: string[];
  onPersist: (opts: { finalTotalCents?: number | null; note?: string | null }) => Promise<PersistOutcome>;
  /** T409 park: save the capture for later WITHOUT recording a quote.
   *  `noteDraft` = the screen's visible Note text (rework 2: it must travel —
   *  a typed note silently vanishing on park broke closed-list item 1). It
   *  lands in booking.note, the capture slot — never a synthetic quote row. */
  onPark: (noteDraft: string | null) => Promise<ParkOutcome>;
  /** T660 M5 — `email` is the OPERATOR-typed address (job.operatorEmail). It is
   *  lifted at the same four call sites as name/phone, and it is a third
   *  positional rather than a field on some customer object so that a caller
   *  cannot pass it by accident from the wrong source. */
  /** ⛔ FOUR ARGUMENTS SINCE BOARD 2089 M3, and the fourth is REQUIRED: the
   *  chosen ISO country is what the typed number is read under, so a lift
   *  that carried the digits without it would store a different number than
   *  the one Helen watched echo back. Required rather than optional because
   *  the four commit sites are the thing at risk, and a required parameter is
   *  checked by the compiler at every one of them. */
  onSetCustomer: (name: string, phone: string, email: string, phoneCountry: string) => void;
  /** T540 (533) — the journey date/time. Patches the store on change rather
   *  than lifting at save time like name/phone: those are lifted at FOUR call
   *  sites (accept / saveOverride / park / recordAgreed) and a fifth field
   *  lifted at only three would silently drop on the fourth. Plain patch,
   *  never priced — the date moves no money. */
  onSetWhen: (patch: WhenPatch) => void;
  onSetVenue: (venue: string | null) => void;
  onSetOption: (key: keyof QuoteOptions, value: boolean) => void;
  /** Correct the passenger band in place. MUST re-price (the caller pairs it
   *  with fetchQuote) — a band that moves without a re-price is the stale-pair
   *  bug wearing a different hat. */
  onSetBand: (band: string) => void;
  /** T651 M7 (R21) — the REAL headcount beside the band. ⛔ A PLAIN PATCH, never
   *  paired with fetchQuote: the band is the pricing key, so a dispatch count
   *  that re-priced would move the customer's money for a note. */
  onSetPassengersExact: (value: number | null) => void;
  /** T1871 M7 (MERGE BLOCKER) — the count box's COMMIT edge, threaded straight
   *  through to BandControl. The keystroke prop above writes the count; this one
   *  derives the band from the settled value. See the pair in
   *  `client/lib/actions.ts` for the money defect the split fixes. */
  onCommitPassengersExact?: () => void;
  /** Route back to the Location screen. Null on the manual path (which never
   *  visits Location) — the link then does not render. */
  onChangeLocation: (() => void) | null;
  /** T400 — per-end driver-details writes (manual path renders the capture
   *  block HERE because it never visits Addons). Plain patch, never priced. */
  onSetDriverEnd: (patch: DriverEndPatch) => void;
  /** A re-price is in flight: the total on screen is the PREVIOUS band's, so
   *  nothing may be saved and the total must not read as final. */
  quotePending: boolean;
  /** T373 M5 — the last agreed price(s) to this place (off-gazetteer / estimate
   *  journeys only). Absent/empty for a first-ever place → the line does not
   *  render (presentation only; optional so it degrades silently). */
  precedent?: { bookingId: number; agreedCents: number }[];
  /** Team-alert plumbing (D17b) — name/phone from THIS screen's capture. */
  onPreviewAlert: (name: string, phone: string) => ReturnType<TeamAlertProps['onPreview']>;
  onSendAlert: TeamAlertProps['onSendAlert'];
  onDone: () => void;
};

/**
 * The false-merge refusal, T660 M5 / P0-12 + SEC-5.
 *
 * ⛔ IT NAMES NO THIRD PARTY, and that is the requirement, not a style choice.
 * Saying whose record holds the address — a name, a number, or even echoing the
 * address back — would turn this box into an enumeration oracle: type any
 * address, learn whether that person is an ECMB customer. The CONDITION is
 * disclosed; the PARTY is not.
 *
 * It states what was written (nothing) and names the two ways out, which is what
 * makes the refusal an explicit CHOICE rather than a dead end: correct the
 * address, or clear it and save without one. Neither is a new button — the
 * capture field is already on screen and Accept is already the primary action —
 * which matters on a screen whose hard constraint is "no more buttons".
 */
export const EMAIL_CONFLICT_COPY =
  'That email address is already on file for a different customer, so nothing was saved. ' +
  'Check the address with the customer and try again, or clear the email and save without it.';

/**
 * ⛔ T1871 M7 - BOARD ASK 805, "Refuse disagreement at the save door", said on
 * the screen. ONE STRING FOR BOTH ACTIONS (Accept and Save for later), because
 * the server refuses the same pair at all three doors and two sentences for one
 * rule is how one of them stops being edited.
 *
 * It states what was written (nothing) and names the two ways out, exactly as
 * the refusal above does: change the band, or change the count. Neither is a new
 * control - both are already on screen - and it must NOT read as transient: the
 * same pairing is refused every time, so "try again in a moment" would be an
 * instruction that can never succeed.
 *
 * ⚠️ IT QUOTES NEITHER VALUE, matching the server's own sentence. Helen has both
 * numbers in front of her; what she does not have is the fact that they cannot
 * both be stored. ⚠️ ASCII HYPHENS ONLY - check-copy.sh scans all of src/client
 * for the em dash - and no band literal, which the same gate bans here.
 */
export const BAND_COUNT_MISMATCH_COPY =
  'The passenger band and the number of passengers do not match, so nothing was saved. ' +
  'Correct one of them, then save again.';

/**
 * ⭐ board 2871 - THE RETURN DATE AND TIME WERE NOT SAVED, one sentence per cause,
 * for BOTH actions (Accept and Save for later). Each is true whether the screen
 * refused before posting or the server refused with its coded 400.
 * ⭐ Captain's ruling (C3, C6): said ON the Return date field (its `error`), never
 * in the top banner far from it, and in the kiosk's word, "trip".
 */
export const RETURN_LEG_COPY: Readonly<Record<ReturnLegCause, string>> = {
  before: 'The return is before the trip, so this was not saved. Check the return date and time.',
  too_far: 'The return is a year or more after the trip, so this was not saved. Check the year.',
  invalid: 'Enter both the return date and time, or leave both empty. This was not saved.',
};

/**
 * ⭐ board 2871 (Captain re-test, item 1) - THE DAY CAME FROM THE CUSTOMER, AND
 * NOTHING IS SAVED YET. An untouched prefilled date is a default, not input: with
 * the time empty nothing is sent (both-empty is allowed), so the screen says so
 * plainly rather than letting a filled date read as a saved return. null when
 * there is a stored return, when Helen typed a date, or once a time is entered.
 */
export function customerReturnHint(job: JobState): string | null {
  if (!job.options.returnTrip || job.returnAt !== null) return null;
  if ((job.returnDraft?.day ?? null) !== null || !isReturnDay(job.returnDate)) return null;
  if (shownReturnLeg(job).time !== '') return null;
  return `The customer said ${whenText(job.returnDate, null)}. Add a time to save the return.`;
}

export function QuoteScreen(props: QuoteScreenProps) {
  const { job } = props;
  const quote = job.quote;
  // T399 item 5: the internal "Alert the team" control is hidden behind a
  // default-OFF setting until the dispatcher phase. The flag rides the existing
  // app_meta → /api/options → registry idiom (same as the managed-rules keys);
  // an absent key reads OFF, so the control is invisible on the kiosk today
  // while every notify path stays wired. The customer-inquiry WhatsApp alert
  // (server-side, POST /api/inquiry) is a DIFFERENT path and is untouched.
  const teamAlertEnabled =
    ((props.registry.options.meta ?? {}) as Record<string, string>)['team_alert_enabled'] === 'true';
  // Hand-priced: what the ENGINE says, when it has said anything (T387 M3 —
  // a manual-mode service with a standing venue rate arrives 'priced' and must
  // take the priced path). The null-quote guard is load-bearing: a failed or
  // in-flight re-price (fetchQuote's catch sets quote null) falls back to the
  // REGISTRY mode — otherwise a wedding whose re-price fails would lose the
  // entire Record-agreed-price UI and the bottom bar.
  /**
   * ⛔⛔ T1871 M5 — AN OVER-BAND JOB IS HAND-PRICED BY CONSTRUCTION, and that
   * arm is FIRST because nothing else can supply it.
   *
   * `fetchQuote` now suppresses the POST for a band the engine cannot price, so
   * `quote` stays null on an over-band job and the expression below falls back
   * to `props.isManual` — false for grid and p2p. That false is exactly why the
   * hand-price block, the Accept/Override block and the TotalBar were all absent
   * on the screen Helen was sent to: she could see the job and could not price
   * it. docs/pricing-spec.md:97 already RULES that an over-band job is priced by
   * judgment, so this arm states the rule the engine's own 400 implies.
   *
   * ⭐ IT UNLOCKS NO NEW CONTROLS — every control it reveals already exists on
   * the manual path, which is why control-budget.spec.tsx (Quote pinned at
   * exactly 21, on a walk-in airport PRICED fixture that is not over-band) is
   * untouched.
   *
   * ⛔ IT ALSO SILENCES A LIE, and that is a consequence to keep, not a side
   * effect: the "Getting the price failed. Pick the passenger band again to
   * retry." banner below is gated on `!handPriced`, and under the suppression it
   * would otherwise fire on EVERY over-band job — where nothing failed and
   * re-picking a band would not help. The pin for that is a test asserting the
   * banner's absence, not a redundant conjunct on the banner itself.
   */
  const overBand = job.band === OVER_BAND;
  const handPriced = overBand || (quote ? quote.status === 'manual' : props.isManual);
  // The priced path now carries the same tall edit surface the manual path has
  // (band chips + requirements), so it takes the SAME fixed bottom bar — the
  // primary action and the price stay glued together and on-screen, never found
  // by scrolling (§8; the M5 ux-review caught Accept below the fold otherwise).
  // A bar shows on any priced quote and on every hand-priced job; a failed
  // re-price (quote === null, not hand-priced) has no action and reserves none.
  const hasBar = handPriced || quote !== null;
  /**
   * T660 M6 (R4) — WHICH line label may take the operator collapse, and the ONE
   * place that decides it. Same PROVENANCE shape as the gates beside it:
   * `pickupEndDisplay` (store/selectors.ts) on the live job's pickup end,
   * `notAMiddleStop`/`isQuotedRouteLabel` (BookingDetail) per persisted stop.
   *
   * `quote.lines[].label` is NOT always a stored `rates.location_label`. The
   * ENGINE composes most of them, using an ordinary sentence separator rather
   * than a hub boundary (src/engine/quote.ts):
   *   :50  'Estimated airport transfer · <place>'   off-grid / anchored estimate
   *   :83  'Estimated transfer · <from> → <to>'     the dormant p2p arm
   *   :109 / :156 '<Service> transfer · <locationId|venueSlug>'  label fallbacks
   * ⚠️ T1723 M4 moved those four separators from an em dash to ' · ' (II-flow-F4,
   * they render live on this screen). The GATE HERE IS UNCHANGED and must stay:
   * the first still interpolates `estimate.anchoredPlace` — OPERATOR-TYPED FREE
   * TEXT — so 'Estimated airport transfer · The K Club — Straffan' still carries
   * a boundary byte this file must not treat as a hub, and rendered
   * '… → The K Club → Straffan'. That is the corruption the other three M6 call
   * sites are gated against; this line item was mis-read as a stored label.
   *
   * A hub boundary can only reach this list from ONE branch — the grid price at
   * quote.ts:106-110, whose label is `snap.labels.get('<service>|<locationId>')`,
   * i.e. the row's own `rates.location_label` (`TEXT NOT NULL`, migrations.ts,
   * so the `??` fallback beside it is unreachable for a priced row). All three
   * conjuncts narrow to that branch, each excluding a different producer — but
   * only ONE of the three is separately falsifiable, and the other two say so
   * (T660 M6b: each conjunct was dropped on its own and `vitest run test/client`
   * re-run — the honest result, not the intended one):
   *   - `quote.status === 'priced'` — set at quote.ts:111 (grid) and :158
   *     (a manual service's standing venue rate) and NOWHERE else, so it
   *     excludes both estimate labels (:50/:83 set 'estimated') and every
   *     hand-priced job ('manual').
   *     ⚠️ REDUNDANT TODAY, and NOT pinned — dropping it reds NOTHING (787/787
   *     still green, re-measured WITH the new pins), because no producer can
   *     pair a golf service with a
   *     non-priced base LINE: golf has no distance model, so its only base-line
   *     branch IS :111, and `job.service` cannot move without `resetForNewQuote`
   *     blanking the quote. Kept as the guard for the day golf gains a second
   *     producer; what is pinned is the PREMISE it rests on
   *     (test/client/operator-em-dash.spec.tsx, 'golf emits NO base line unless
   *     it is priced'), which reds that same afternoon.
   *   - `job.service === 'golf'` — golf is a GRID service (shared/service-schema
   *     pricingMode), so 'priced' + golf can only be :111, never the venue arm
   *     at :158 (wedding / private-tour / nursing-home). It is also the same
   *     conjunct `pickupEndDisplay` leads with: the hub boundary is a golf-rates
   *     construct, composed `${hubName} — ${routeName}` by the two label writers
   *     and minted by no other service.
   *   - `l.kind === 'base'` — the rate line itself, not the variance lines
   *     stacked on it (engine/variance.ts mints 'addon'/'return'; an override
   *     mints 'override'), whose labels are plain copy.
   *     ⚠️ Also NOT pinned, for the same measured reason: dropping it reds
   *     nothing either, because every other label the engine can stack here is
   *     a fixed string with no boundary in it ('Meet & greet', 'Extra pickups
   *     (n)', 'Return trip', 'Trailer', 'Manual override'), so both renderings
   *     are byte-identical on them. Defence-in-depth against a future line
   *     label built from a place name — NOT a tested behaviour today.
   *
   * Everything else keeps `formatRouteLabel`, the pre-M6 rendering, which touches
   * only the em-dash + arrow PAIR and so cannot corrupt a lone dash. The GOLF
   * conjunct is the pinned one, both ways, in
   * test/client/operator-em-dash.spec.tsx over engine-composed labels: dropping
   * it reds 'leaves a PRICED VENUE rate line byte-verbatim' with
   * 'Druids Glen → clubhouse' where an operator typed 'Druids Glen — clubhouse'.
   */
  const pricedGolfRate = quote?.status === 'priced' && job.service === 'golf';
  const displayLineLabel = (l: QuoteLine): string =>
    pricedGolfRate && l.kind === 'base' ? displayRouteLabel(l.label) : formatRouteLabel(l.label);
  // A walk-in / phone-in has no customer yet: the capture step is a
  // first-class, always-visible part of the quote step (D17d).
  const isWalkIn = job.prefillSource === null && job.bookingId === null;
  // T409: park is a PRE-quote save only. A reopened quoted booking carries
  // previousQuoteCents; an in-session save sets doneOutcome 'saved' — both
  // hide the affordance (the server 409s any residue). A parked job may be
  // parked again: re-parking updates the saved capture.
  const canPark = job.previousQuoteCents === null && job.doneOutcome !== 'saved';
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideEuros, setOverrideEuros] = useState('');
  const [overrideNote, setOverrideNote] = useState('');
  // Re-opening a hand-priced booking pre-fills the LAST agreed price so Helen
  // adjusts it rather than re-keying it from memory (M7 functional pass).
  const [agreedEuros, setAgreedEuros] = useState(
    job.previousQuoteCents != null ? String(job.previousQuoteCents / 100) : '',
  );
  const [agreedNote, setAgreedNote] = useState('');
  const [nameWarned, setNameWarned] = useState(false);
  // T409 park: the phone is nudged ONCE, never a wall (the phone never
  // closes) — a second tap saves without it. Re-arms per screen mount.
  const [parkNudged, setParkNudged] = useState(false);
  // Q36 order-of-magnitude override typo-guard (soft, dismissable). Re-armed
  // when the amount is edited.
  const [amountWarned, setAmountWarned] = useState(false);
  const [name, setName] = useState(job.customerName);
  const [phone, setPhone] = useState(job.customerPhone);
  /* ⭐ BOARD 2089 M3 — the ISO country the typed number is read under. LOCAL
     state seeded from the job, exactly like `phone` above, and lifted through
     the SAME `onSetCustomer` call at the same four commit sites: a fifth capture
     value lifted on its own channel is how one of the four ends up dropping it,
     which is the risk `CallInCapture`'s `WhenPatch` header already records.
     ⛔ IT IS A REQUIRED FOURTH ARGUMENT rather than an optional one, so the
     compiler — not a reviewer — is what proves all four sites carry it. */
  const [phoneCountry, setPhoneCountry] = useState(job.customerPhoneCountry);
  // T660 M5 — seeded from job.operatorEmail (Helen's own typing, restored on a
  // re-render), NEVER from job.customerEmail. Seeding it from the latter would
  // put the public form's address into the capture POST by a different route,
  // which is the whole thing the separate store field exists to prevent.
  const [email, setEmail] = useState(job.operatorEmail);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * K5-4 (DESIGN.md §7.1: no unexplained moment) — WHICH money commit is in the
   * air, so the fixed bar can say so in words while the request flies.
   *
   * ⛔ NOT `busy`, and that is the whole reason this exists. `busy` is shared by
   * Accept, Save override AND Save for later (`finish` and `park` both set it),
   * and Save for later renders on the SAME screen as the hand-quote bar — so a
   * park would relabel that bar 'Recording…' for a save that records no price.
   * Set immediately around the commit, cleared in a `finally` so a throw cannot
   * strand the label on the in-flight word.
   */
  const [committing, setCommitting] = useState<'accept' | 'record' | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  /**
   * ⛔⛔ SETTING A FAILURE IS NOT SHOWING ONE — THE ANNOUNCEMENT HAS TO TAKE
   * FOCUS (t651 M8 round B ux-review, BLOCK; the THIRD instance of this defect
   * class on this program and the one that got escalated instead of fixed).
   *
   * MEASURED on the served kiosk, on the money path: a hand-priced nursing
   * quote, the agreed-price box cleared, `.content` scrolled near its max
   * (scrollTop 1642 of ~1672, clientHeight 900 — where Helen naturally IS,
   * because the box sits deep in a tall form) and then "Record agreed price"
   * tapped in the fixed bottom bar. `recordAgreed`'s empty-price banner rendered
   * at top: -1324px relative to `.content`, `document.activeElement` stayed the
   * Record button, and NOTHING on screen changed. Helen taps Record mid-call,
   * sees nothing happen, and may believe the booking saved and hang up.
   *
   * ⛔ FIXED ONCE, FOR EVERY BRANCH, DELIBERATELY. Only ONE failure on this
   * screen used to self-heal — the walk-in name gate, and only incidentally,
   * because focusing the name field scrolls it into view. Every other branch
   * (`recordAgreed`'s empty price, all four `finish()` outcomes, all four
   * `saveOverride` guards, and all five `park` paths) had no such compensation.
   * A per-branch patch would have left the next one broken, so `fail()` is now
   * the ONLY way a failure string is set and every one of them announces.
   *
   * ⛔ THE MECHANISM IS THE REPO'S, NOT A NEW ONE. `Banner` grew `elementRef` +
   * `tabIndex` in T642 M6 round B for exactly this, `PriceList.tsx` uses it, and
   * the nursing capture screen (deleted at board 2814 M6) fixed the identical
   * BLOCK one milestone ago with the same two pieces. Focusing a `tabIndex={-1}` target scrolls it into
   * view natively, so one call fixes the caret AND the visibility. No
   * `scrollIntoView` anywhere: the in-repo pattern already does the job.
   *
   * ⛔ AND `fail()` IS THE ONLY WAY IN, ENFORCED BY THE TYPE — not by a comment
   * asking nicely. `failure` holds a `Failure` OBJECT, so the seventeenth branch
   * someone adds cannot write `setFailure('…')`: it does not compile. That is
   * deliberate. MEASURED at authoring time: with the object state in place, a
   * branch reverted to a bare string set is a tsc error; with a plain
   * `string | null` state it compiled, still announced (the effect fires on any
   * value change) and only lost the RE-ANNOUNCE — so the convention would have
   * degraded silently, one branch at a time, which is how this class keeps
   * coming back. The one thing a comment cannot pin, the type does.
   *
   * ⚠️ A FRESH OBJECT PER CALL IS WHAT MAKES EVERY ATTEMPT RE-ANNOUNCE, and it
   * is doing the job the deleted nursing capture screen's separate `failureSeq`
   * counter did there. That screen held a STRING, so re-tapping Continue with the same
   * error set an identical value, Preact bailed out of the re-render, the effect
   * never re-ran, and the operator was once again staring at an unchanged button
   * after scrolling back down. Here `fail()` allocates, Preact compares with
   * `!==`, and the `[failure]` dependency therefore fires on EVERY failed
   * attempt. ⛔ Do not "optimise" this into reusing the previous object or
   * memoising on the text: that reintroduces the bug with no visible edit.
   *
   * ⚠️ TWO REFS, NOT ONE SHARED REF ACROSS THE TWO BANNERS. The failure string
   * has two mutually-exclusive render sites (the top banner, and the copy inside
   * the override panel that M3 ux-review put beside the fields it describes). A
   * single ref handed to both would depend on Preact's mount/unmount ref
   * ordering when `overrideOpen` flips — and if the unmounting site nulls the
   * ref after the mounting one set it, the handle stays null and the fix
   * silently stops working, which is this very defect wearing a disguise.
   *
   * ⚠️ THE NAME GATES STILL FOCUS THE NAME FIELD, and that is not the old
   * behaviour surviving by luck — it is now explicit. `nameWarned` is sticky
   * (nothing ever clears it), so keying focus off it would send EVERY later
   * failure to an already-filled name box. The target travels with the call
   * instead, and falls back to the banner when the capture block is not rendered
   * (a prefilled job has no name input). Shape borrowed from `Operators.tsx:168`
   * (`(credentialRef.current ?? noticeRef.current)?.focus()`).
   */
  const failureRef = useRef<HTMLDivElement>(null);
  const overrideFailureRef = useRef<HTMLDivElement>(null);
  const fail = (text: string, target: FailureTarget = 'banner') => setFailure({ text, target });
  useEffect(() => {
    if (failure === null) return;
    const banner = overrideOpen ? overrideFailureRef.current : failureRef.current;
    const field = failure.target === 'name' ? nameInputRef.current : null;
    (field ?? banner)?.focus();
  }, [failure]);

  /**
   * ⭐ board 2871 (Captain's ruling, C3) - A REFUSED RETURN IS SAID ON ITS FIELD. The
   * cause becomes Return date's `error` (so it is `aria-invalid` and described by
   * the sentence), Return time is described by the same sentence, and the caret
   * and the view go to Return date: the top banner sat far from the fields.
   * A fresh object per refusal, so a second refusal re-focuses (the `fail()` rule).
   * Cleared by any Return date / Return time input and by switching the return off.
   */
  const [returnError, setReturnError] = useState<{ cause: ReturnLegCause } | null>(null);
  const returnDateRef = useRef<HTMLInputElement>(null);
  const refuseReturn = (cause: ReturnLegCause) => setReturnError({ cause });
  useEffect(() => {
    if (returnError === null) return;
    const el = returnDateRef.current;
    el?.scrollIntoView?.({ block: 'center' });
    el?.focus();
  }, [returnError]);
  useEffect(() => {
    if (!job.options.returnTrip) setReturnError(null);
  }, [job.options.returnTrip]);

  /* ⛔ THE CAPTURED-PRICE PREFILL (T651 M8, R12/R13) WAS HERE and went with the
     nursing tile at board 2814 M6 (DD-8): no nursing job reaches this screen. */

  /**
   * ⛔⛔ T1949 REWORK 2 (Captain live BLOCK, journey 1) — THE BANNER WITHOUT
   * THIS EFFECT IS A NOTICE OVER A LOADED GUN.
   *
   * MEASURED on a served kiosk, the flow Helen actually uses: a 25-passenger
   * booking hand-priced at EUR 800, saved, re-opened from Bookings, count
   * corrected to 10. The band re-derived to `8-12`, the store cleared the hand
   * price, and the announcement above rendered correctly — AND THE BOX STILL
   * READ 800 (a DOM `value`, not a placeholder), the Agreed total still read
   * 800, and pressing "Record agreed price" SAVED 800 at `8-12` for 10
   * passengers. That is the original harm ask 823 exists to prevent, now
   * wearing a banner a busy operator can miss mid-call.
   *
   * ⛔ THE STORE CLEAR CANNOT REACH THE BOX, AND THAT IS THE WHOLE DEFECT.
   * `agreedEuros` is LOCAL component state, seeded ONCE at mount from
   * `job.previousQuoteCents` (the M7 re-open prefill above). `setBand`'s
   * band-change carry nulls `job.finalTotalCents` and writes
   * `handPriceClearedCents`, but it has no way to touch a `useState` in a
   * screen that never unmounts across the change — and `recordAgreed` reads
   * `agreedEuros`, NEVER `job.finalTotalCents`. So the two halves disagreed:
   * the store had forgotten the price and the input had not.
   *
   * ⭐ AN EFFECT ON THE TRANSITION, NOT A DERIVED VALUE. Deriving the input from
   * `job.finalTotalCents` would close it too, and was rejected: the box is
   * Helen's typing buffer (she keys "8", "85", "850" — each an intermediate
   * that is not the job's money), so a controlled value read back off the store
   * either fights her keystrokes or needs a write on every one. VenueControl's
   * Other-reveal bug (`isKnown ? '' : value` blanking a controlled input
   * mid-keystroke) is what that shape looks like when it goes wrong, and the
   * prefill effect above already refuses to do it.
   *
   * ⛔ THE DEP LIST IS THE TRANSITION TEST — no `useRef` bookkeeping, because
   * the effect fires only when `handPriceClearedCents` CHANGES:
   *   - mount with null          -> the guard returns; the M7 seed SURVIVES.
   *     ⚠️ Without that guard this effect would blank the prefill on every
   *     ordinary re-open, which is the R12 behaviour it must not cost.
   *   - null -> 80000 (a clear)  -> blank. The case above.
   *   - 80000 -> null            -> the guard returns. A later band change that
   *     clears nothing, or a save, must NOT wipe the new figure she has keyed.
   *   - 80000 -> 55000           -> blank. Unreachable today (a second clear
   *     needs `wasHandQuoted` true again, and every writer that restores it
   *     passes through null first) — but graded by the dep, not by that
   *     argument, so a future writer cannot quietly make it wrong.
   *   - a keystroke after a clear -> the dep did not move, so the effect does
   *     not re-fire and her typing stands.
   *
   * ⛔ THE BANNER STAYS. Blanking is not a substitute for saying what went:
   * ask 823 rules that Helen is TOLD the amount, and an empty box alone would
   * be the silent clear this task was opened to end.
   *
   * NO NEW GUARD IN `recordAgreed`, deliberately: it already refuses
   * `!Number.isFinite(euros) || euros <= 0`, and `Number('')` is 0, so an empty
   * box cannot post. `agreedCents` goes null by the same route, so the Agreed
   * total in the bottom bar empties with it — one edit, both surfaces.
   *
   * MUTATION CHECK, RUN: delete this effect and the first arm of `(g4)` in
   * `test/client/over-band-hand-price.spec.tsx` reds at the box ("expected
   * '800' to be ''"). ⭐ AND THE MONEY HARM IS REAL BEHIND IT, MEASURED rather
   * than argued: with that DOM assertion lifted, the same click calls
   * `onPersist` with `{finalTotalCents: 80000}` — the CLEARED amount, at band
   * `8-12`, for 10 passengers. The arm grades the post, not only the pixel.
   */
  useEffect(() => {
    if (job.handPriceClearedCents === null) return;
    setAgreedEuros('');
  }, [job.handPriceClearedCents]);

  const finish = async (opts: { finalTotalCents?: number | null; note?: string | null }) => {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    const outcome = await props.onPersist(opts);
    setBusy(false);
    switch (outcome.kind) {
      case 'saved':
      case 'price-check':
        props.onDone();
        return;
      case 'mismatch':
        fail("This quote doesn't match the saved booking. Start a fresh quote from the home screen.");
        return;
      // Nothing was written, so this must NOT go to Done — 'saved' and
      // 'price-check' both do, and landing there would tell Helen the job was
      // handled when the price on screen is still the old band's.
      case 'price-changed':
        fail('The price changed since this quote was calculated. Re-pick the passenger band to refresh the price, then save again.');
        return;
      // ⛔ T1871 M6 REVIEW (security MEDIUM) - A DETERMINISTIC REFUSAL MUST NOT
      // RENDER AS A TRANSIENT ONE. This landed in the 'error' arm below, whose
      // sentence is "Try again in a moment" - and the same amount is refused
      // every single time, so that is an instruction which can never succeed.
      // The measured case is the fat-fingered extra zero on the agreed price
      // (1500000 for 150000): recordAgreed has NO magnitude soft-confirm - only
      // saveOverride does, and only when there is a computed price to compare
      // against - so this copy is the one thing that tells Helen the amount is
      // the problem before she gives up on a price she has already agreed.
      // BOTH DIRECTIONS NAMED: the server floor refuses a sub-cent entry (which
      // rounds to zero cents) as well as an oversized one.
      case 'price-out-of-range':
        fail('That price is too large or too small to save. Check the amount, then save again.');
        return;
      // ⛔ T1871 M7 (ask 805) - DETERMINISTIC, so it never reaches the 'error'
      // arm's "Try again in a moment", for the reason 'price-out-of-range'
      // above does not. Nothing was written, so this must NOT go to Done.
      case 'band-count-mismatch':
        fail(BAND_COUNT_MISMATCH_COPY);
        return;
      // ⭐ board 2871 - deterministic, and nothing was written: never Done. Said on
      // the Return date field (C3), not in the top banner.
      case 'return-invalid':
        refuseReturn(outcome.cause);
        return;
      case 'pending':
        fail('The price is still updating. Wait a moment, then save again.');
        return;
      // T660 M5 / P0-12. NOTHING WAS SAVED — the server refused before it wrote
      // anything — so this must not go to Done, for the same reason
      // 'price-changed' does not.
      // T730 compose: T660 raised this with a bare setFailure; T651's fail()
      // is now the only way in, so the refusal ANNOUNCES like every other
      // branch. Without this it renders off-screen exactly as the M8 banner did.
      case 'email-conflict':
        fail(EMAIL_CONFLICT_COPY);
        return;
      case 'error':
        fail("Saving didn't work. The quote is still on screen. Try again in a moment.");
    }
  };

  const accept = async () => {
    // Walk-in name gate: the FIRST nameless Accept warns and focuses the
    // name field (no silent save); a second nameless Accept proceeds as an
    // honest price check via the Done copy.
    if (job.bookingId === null && !name.trim() && !nameWarned) {
      setNameWarned(true);
      fail('Add the customer name to save this quote, or Accept again for a price check only.', 'name');
      return;
    }
    setFailure(null);
    props.onSetCustomer(name.trim(), phone.trim(), email.trim(), phoneCountry);
    setCommitting('accept');
    try {
      await finish({});
    } finally {
      setCommitting(null);
    }
  };

  const saveOverride = async () => {
    const euros = Number(overrideEuros);
    if (!Number.isFinite(euros) || euros <= 0) {
      fail('Enter the agreed price in euro before saving.');
      return;
    }
    if (quote?.totalCents != null && !overrideNote.trim()) {
      fail('A note is required when overriding a computed price.');
      return;
    }
    // Same name-gate as Accept, but HARD: a nameless walk-in override would
    // be silently discarded by the price-check path — never let an agreed
    // price vanish (M3 code-review WARN 1).
    if (job.bookingId === null && !name.trim()) {
      setNameWarned(true);
      fail('An agreed price needs a customer name to be saved. Add the name, then save again.', 'name');
      return;
    }
    // Q36 soft-confirm (NOT a security control): an override off the computed
    // price by an order of magnitude is almost always a typo (a decimal slip or a
    // stray zero). Warn ONCE, dismissable - a second Save override proceeds; the
    // flag re-arms when the amount is edited. 10x threshold on purpose: a tighter
    // one would trip legitimate within-magnitude overrides. FB6: the boundary is
    // INCLUSIVE (>=/<=) so the exactly-x10 added-zero fat-finger (€105 -> €1050:
    // 105000 == 10*10500) and its dropped-zero mirror are caught, not skated over.
    const overrideCents = Math.round(euros * 100);
    if (
      quote?.totalCents != null &&
      (overrideCents >= 10 * quote.totalCents || overrideCents * 10 <= quote.totalCents) &&
      !amountWarned
    ) {
      setAmountWarned(true);
      fail('That looks far from the calculated price. Check the amount, then save again to confirm.');
      return;
    }
    props.onSetCustomer(name.trim(), phone.trim(), email.trim(), phoneCountry);
    await finish({ finalTotalCents: overrideCents, note: overrideNote.trim() || null });
  };

  /**
   * T409 park ("come back to me with a quote in the next hour"): save the
   * captured state, no price recorded. Name gates it exactly like the save
   * ("add the name to save it"); a missing phone nudges once and never blocks.
   */
  const park = async () => {
    if (busy) return;
    if (!name.trim()) {
      setNameWarned(true);
      fail('Add the customer name to save this quote for later.', 'name');
      return;
    }
    if (!phone.trim() && !parkNudged) {
      setParkNudged(true);
      fail('No phone number yet. Add one if you have it, or tap Save for later again to save without it.');
      return;
    }
    setFailure(null);
    props.onSetCustomer(name.trim(), phone.trim(), email.trim(), phoneCountry);
    setBusy(true);
    // The visible Note field (hand-price path) rides the park — empty means
    // "nothing typed", and the action then keeps the job's existing capture note.
    const outcome = await props.onPark(agreedNote.trim() || null);
    setBusy(false);
    switch (outcome.kind) {
      case 'parked':
        props.onDone();
        return;
      case 'name-required':
        setNameWarned(true);
        fail('Add the customer name to save this quote for later.', 'name');
        return;
      case 'conflict':
        fail('This job cannot be saved for later. It may already be quoted. Start a fresh quote from the home screen.');
        return;
      // Park lazy-creates through the SAME createBookingForJob, so it meets the
      // same refusal. Pinning only the Accept path would let a future split
      // reintroduce the false merge on the unpinned one.
      // T730 compose: fail(), not setFailure() — see the Accept arm above.
      case 'email-conflict':
        fail(EMAIL_CONFLICT_COPY);
        return;
      // The park door validates the band/count pair exactly as the quote door
      // does (ask 805), and the lazy-create can refuse it here too - so the arm
      // exists on BOTH switches. Pinning only Accept would leave the park path
      // answering "try again in a moment" to a refusal that never relents.
      case 'band-count-mismatch':
        fail(BAND_COUNT_MISMATCH_COPY);
        return;
      case 'return-invalid':
        refuseReturn(outcome.cause);
        return;
      case 'error':
        fail("Saving didn't work. The quote is still on screen. Try again in a moment.");
    }
  };

  const recordAgreed = async () => {
    const euros = Number(agreedEuros);
    if (!Number.isFinite(euros) || euros <= 0) {
      // ⭐ THE MEASURED BLOCK. This is the branch that rendered 1324px above the
      // viewport with focus left on the Record button in the fixed bottom bar.
      fail('Enter the agreed price in euro before recording.');
      return;
    }
    // HARD name gate (same rule as Override): a hand-typed agreed price plus
    // the venue and requirement toggles must never be silently discarded by
    // the nameless price-check path — recording always needs a name.
    if (job.bookingId === null && !name.trim()) {
      setNameWarned(true);
      fail('An agreed price needs a customer name to be saved. Add the name, then record again.', 'name');
      return;
    }
    setFailure(null);
    props.onSetCustomer(name.trim(), phone.trim(), email.trim(), phoneCountry);
    setCommitting('record');
    try {
      await finish({ finalTotalCents: Math.round(euros * 100), note: agreedNote.trim() || null });
    } finally {
      setCommitting(null);
    }
  };

  /** K5-7 — see `summaryWithDraftName`: `props.summary` with its head swapped
   *  for the draft name while one is being typed. */
  const restatement = summaryWithDraftName(props.summary, name, job.customerName);

  const agreedCents = (() => {
    const euros = Number(agreedEuros);
    return Number.isFinite(euros) && euros > 0 ? Math.round(euros * 100) : null;
  })();

  return (
    <div class={hasBar ? 'screen-pane screen-pane--with-bar screen-pane--with-bar--quote' : 'screen-pane'}>
      <h1 class="screen-title" tabIndex={-1}>Quote</h1>

      {/* T660 M8 (P0-24 / P0-25) — THE ONE SUMMARY CARD.
          Three restatement surfaces that were three separate blocks, each with
          its own chrome and its own margin — journey 56px + breakdown 70px +
          total 72px, plus the 32px of inter-block gap between them = 230px on
          the canonical airport walk-in — become one card.

          The CLASS NAMES are deliberately unchanged: `quote-flow.spec.tsx` reads
          `.quote-total`, `.quote-total-label` and `.quote-total-row[aria-busy]`
          by name, and `style-contract.spec.ts`'s P1-3 COLUMN array reads
          `.journey-summary`, `.line-items` and `.quote-total-row`. What they give
          up is their own chrome, and that reset is SCOPED to `.quote-summary` in
          app.css — `.journey-summary` also renders on `Done.tsx` (and rendered
          on the nursing capture screen, deleted at board 2814 M6), so touching
          its base rule would restyle a screen this milestone never measured.

          ⛔ THE CARD IS HEIGHT-BUDGETED AT <= 146px (1024x768, airport walk-in).
          Both graded criteria are functions of the same quantity, `230 - C`:
          SC-4 needs a >=84px reduction (C <= 146) and P0-26 needs the capture
          fields >=82px higher (C <= 148). The smaller binds. DO NOT add a row
          here without re-measuring both — the ceiling is graded first precisely
          so that a row added later cannot quietly spend the fold margin. */}
      {/* ⭐ T1723 M5 (M4 code-review note 2) - THE CARD GATES ON `restatement`,
          NOT ON `props.summary`. Widening only, and provably so: every branch of
          `summaryWithDraftName` returns its `summary` argument unchanged unless a
          DRAFT NAME is typed, in which case it returns a non-empty string, so
          `restatement` is truthy everywhere `props.summary` was and in one place
          more. That one place is the whole finding (see the head gate below).
          ⚠️ It adds no ROW to the card and so spends none of the height budget
          above: in the state it newly renders, the card holds the restatement
          line alone. */}
      {(restatement || (quote !== null && quote.lines.length > 0) || quote?.totalCents != null) && (
        <section class="quote-summary" aria-label="Quote summary">
          {/* ⚠️ The head renders for EITHER half. Gating it on `props.summary`
              alone silently made the route back to Location depend on the
              restatement TEXT being non-empty, which is an unrelated condition;
              `quote-edit-in-place.spec.tsx:99` renders with `summary: ''` and
              caught that. Before M8 the affordance hung off `onChangeLocation`
              only; it still does.

              ⛔ T1723 M5 (M4 code-review note 2) - THE FIRST HALF NOW TESTS
              `restatement`, THE THING THIS BLOCK RENDERS, not `props.summary`,
              the string it is derived FROM. The two come apart in exactly one
              state and it is a reachable one: the MANUAL path passes
              `onChangeLocation: null` (it never visits Location), so with an
              empty `props.summary` both operands were false while Helen had
              typed a name and `restatement` held it. K5-7's whole point is that
              the restatement leads with the name she has typed but not yet
              committed, and the gate deleted it without a trace. A condition
              that renders X must be about X. */}
          {(restatement || props.onChangeLocation) && (
            <div class="quote-summary-head">
              {/* K5-7 (DESIGN.md §6): the restatement leads with the name Helen
                has TYPED, not only the one already committed. The draft is
                LOCAL state here (name/phone are lifted at the four commit call
                sites, deliberately), so the store selector cannot see it and the
                swap happens on the string it produced. Identical to
                `props.summary` until she types something different. */}
            {restatement && <p class="journey-summary">{restatement}</p>}
              {/* The ONE route back to Location. It previously lived on
                  CustomerInfoBlock's Journey row, which renders only on the
                  PREFILLED shape — so a walk-in had a journey restated on screen
                  and no affordance to change it. On the card it is reachable on
                  every shape and there is still exactly one of it, which is what
                  "one summary card with an Edit affordance" means on a screen
                  whose hard constraint is "no more buttons".
                  The accessible name is UNCHANGED ('Change the journey') because
                  `quote-edit-in-place.spec.tsx:257` and `:265` grade presence and
                  absence by that name through QuoteScreen — those pins now grade
                  the card instead of the block, which is a better pin, not a
                  weaker one. */}
              {props.onChangeLocation && (
                <button
                  type="button"
                  class="btn btn--quiet btn--inline"
                  aria-label="Change the journey"
                  onClick={props.onChangeLocation}
                >
                  Change
                </button>
              )}
            </div>
          )}

          {quote !== null && quote.lines.length > 0 && (
            <div class="line-items" aria-busy={props.quotePending}>
              {quote.lines.map((l) => (
                <div key={l.label + String(l.cents)} class="line-item">
                  {/* The base fare line is labelled with the location, which on a
                      PRICED GOLF job is the stored route label and on every other
                      shape is engine-composed copy that may carry a typed em dash —
                      see `displayLineLabel` above. Display only. */}
                  <span class="line-label">{displayLineLabel(l)}</span>
                  <span class="line-amount">{euro(l.cents)}</span>
                </div>
              ))}
            </div>
          )}

          {/* While a re-price is in flight, the figures on file are the PREVIOUS
              band's — so the headline total is REPLACED with an explicit status,
              never shown faded-but-readable (a dimmed euro figure still reads as a
              settled price, and this number is what Helen says down the phone). The
              line items dim in sympathy. `job.quote` is deliberately NOT cleared to
              do this: clearing it would flash the failure banner and vanish the
              action row on every keystroke-fast re-price. The word and the aria-busy
              state come from the same prop, so they cannot drift. */}
          {quote?.totalCents != null && (
            <div class="quote-total-row" aria-busy={props.quotePending}>
              <span class="quote-total-label">
                Total{quote.status === 'estimated' && !props.quotePending &&
                  <span class="chip chip--estimate">Estimate</span>}
              </span>
              <span class="quote-total">
                {props.quotePending ? 'Updating…' : euro(quote.totalCents)}
              </span>
            </div>
          )}
        </section>
      )}

      {job.suggestedDuplicateId !== null && (
        <Banner kind="warn">
          This customer might already exist. Check Bookings before saving a duplicate.
        </Banner>
      )}

      {/* Hand-priced jobs never need the engine result — no error/loading
          state for it (the price is typed, not fetched). */}
      {/* The fix is now IN PLACE, not a step back — the band chips and the
          requirement toggles below both re-price, and re-picking the already
          active band re-fetches. Telling her to go back would send her to a
          screen she may never have visited. */}
      {quote === null && !handPriced && (
        <Banner kind="error">
          Getting the price failed. Pick the passenger band again to retry.
        </Banner>
      )}

      {handPriced && (
        <Banner kind="info">
          This journey is priced by hand. Record the agreed price when you have it.
        </Banner>
      )}

      {/* ⭐ T1949 (board ask 823) — SAY THAT THE AGREED PRICE WENT, AND NAME IT.
          Ruling 804 lets a corrected headcount re-derive a pinned band, and the
          re-derivation runs through setBand's band-change carry, which clears an
          agreed hand price. Alex ruled the clear stays and Helen is told, so the
          number never vanishes silently.

          `warn`, not `info`: something she agreed has been taken away and she
          has to act. Existing Banner markup, no new component - the design
          system is board #1749's, never a worker's.

          `euro()` because check:copy bans a bare euro literal in src/client; the
          reason is spelled without a band token for the same gate. */}
      {job.handPriceClearedCents != null && (
        <Banner kind="warn">
          The agreed price of {euro(job.handPriceClearedCents)} was cleared because the passenger
          band changed. Record the new agreed price.
        </Banner>
      )}

      {/* T373 M5 — precedent: the last agreed price(s) to this place, so Helen
          has a number and never needs to substitute a stand-in town to get one.
          No place echo ("this place" refers to the journey already in the summary
          above), so no user string is re-rendered. Styled for >=3:1 contrast —
          the p2/contrast sweep grades the WORST visible € element, and a muted
          grey hint would fail it. `euro()` per check:copy (no € literals). */}
      {(props.precedent ?? []).length > 0 && (
        <p class="quote-precedent">
          Last quoted to this place:{' '}
          {(props.precedent ?? []).map((p, i) => (
            <span key={p.bookingId}>
              {i > 0 ? ', ' : ''}
              <span class="quote-precedent-amount">{euro(p.agreedCents)}</span>
            </span>
          ))}
        </p>
      )}

      {/* T485 — the price THIS booking was already agreed at, on the priced
          path. Until now `previousQuoteCents` had exactly one surface: it seeds
          the agreed-price input, which renders only under `handPriced`. That
          was fine while a re-opened hand-quote always came back hand-priced.
          It no longer does: a booking hand-quoted under a town the old search
          could not reach (a fada-less "Dun Laoghaire") now resolves on re-open,
          gets a grid quote, and drops out of `handPriced` — taking the only
          display of a figure Helen may have ALREADY SENT the customer with it.
          Re-priced is correct; losing sight of the prior number is not. */}
      {job.previousQuoteCents != null && !handPriced && (
        <p class="quote-precedent">
          Already agreed on this booking:{' '}
          <span class="quote-precedent-amount">{euro(job.previousQuoteCents)}</span>
        </p>
      )}

      {quote?.flags
        .filter((f) => f.code !== 'manual-quote')
        .map((f) => (
          <Banner key={f.code} kind={f.severity === 'info' ? 'info' : 'warn'}>{f.message}</Banner>
        ))}

      {/* D17a: everything the customer told us, glanceable where the
          pricing decisions happen.
          T660 M8: `onChangeLocation` is NO LONGER passed. The affordance moved to
          the summary card above, so the journey now has exactly ONE way to be
          changed instead of one-on-the-prefilled-shape-only. Passing it here as
          well would put two controls named 'Change the journey' on the same
          screen, which is both a Playwright strict-mode violation waiting to
          happen and the "too many buttons" failure mode this product exists
          around. */}
      {job.prefillSource !== null && (
        <CustomerInfoBlock job={job} extrasLabels={props.customerExtrasLabels} />
      )}

      {/* When the override panel is open, its validation copy renders INSIDE
          the panel (below) so the error sits beside the fields it describes —
          never between the total and Accept (M3 ux-review).
          ⛔ `elementRef` + `tabIndex` are the FIX, not decoration: see the
          `fail()` block above. Without them this banner renders wherever the
          form happens to have pushed it — measured at -1324px — and the operator
          sees nothing at all. */}
      {failure && !overrideOpen && (
        <Banner kind="error" elementRef={failureRef} tabIndex={-1}>{failure.text}</Banner>
      )}

      {/* D17d: first-class phone-in/walk-in capture — always visible. */}
      {isWalkIn && (
        <CallInCapture
          name={name}
          phone={phone}
          phoneCountry={phoneCountry}
          onPhoneCountry={setPhoneCountry}
          email={email}
          journeyDate={job.journeyDate}
          journeyTime={job.journeyTime}
          nameInputRef={nameInputRef}
          onName={setName}
          onPhone={setPhone}
          onEmail={setEmail}
          onWhen={props.onSetWhen}
        />
      )}

      {/* Venue renders for MANUAL-MODE SERVICES only (wedding/private-tour) —
          grid/p2p journeys never state one. T387 M3: gate on isManual ALONE,
          NOT handPriced — the venue must stay editable on a rates-PRICED
          wedding (it IS the priced input now; hiding it would strand Helen on
          the wrong venue's price). nursing-home is manual too, and never
          reaches this screen since board 2814 M6 (DD-8); the guard stays so an
          old nursing row can never grow a generic "Destination" box. */}
      {props.isManual && job.service !== 'nursing-home' && (
        <VenueControl service={job.service ?? ''} venue={job.venue} onVenue={props.onSetVenue} />
      )}
      {/* T400 — gate on pricingMode-manual (via isManual), NOT the service:
          a re-opened private-tour is manual too (review cycle-1 issue 3). The
          manual flow never visits Addons, so its per-end capture lives here;
          stop rows stay off (manual services carry no billable stop count). */}
      {props.isManual && (
        <DriverDetails job={job} showStops={false} emphasisePickup onSetEnd={props.onSetDriverEnd} onSetStop={() => {}} />
      )}
      {/* T400 item 4 — the priced path captured on Addons; Quote shows the
          quiet copy-friendly record (text only, zero budget cost). */}
      {!props.isManual && <DriverDetailsLines job={job} />}

      {/* Everything the customer stated, correctable HERE — on BOTH paths.
          The band lands on the manual path too, and that is not incidental: a
          manual service with a valid band routes STRAIGHT to Quote, skipping
          Passengers entirely, which is exactly where the reported "actually
          we're 8-12" wedding was trapped.
          Both render even when `quote === null` (a failed re-price), because
          re-picking a band is then the only way back to a price. */}
      <BandControl
        registry={props.registry}
        band={job.band}
        customerBand={job.customerBand}
        onPick={props.onSetBand}
        passengersExact={job.passengersExact}
        onSetPassengersExact={props.onSetPassengersExact}
        onCommitPassengersExact={props.onCommitPassengersExact}
      />
      <Requirements
        registry={props.registry}
        service={job.service ?? ''}
        options={job.options}
        baseline={job.customerFlags}
        onChange={props.onSetOption}
        // ⭐⭐ T1717 M6 (DD6, P0-11) — what the NURSING HOME answered, so an
        // operator override of the return is labelled and warned rather than
        // silent. null on every non-portal job, which renders nothing.
        homeReturnTrip={job.homeReturnTrip}
        // ⭐ board 2871 - when the job comes back, directly under its switch and only
        // while it is on. A plain patch like the journey date: never priced. The
        // first edit copies the shown time in, so a stored return is never blanked.
        returnLeg={job.options.returnTrip ? (
          <>
            <FormField
              id="quote-return-date"
              label="Return date"
              type="date"
              value={shownReturnLeg(job).day}
              elementRef={returnDateRef}
              {...(returnError === null ? {} : { error: RETURN_LEG_COPY[returnError.cause] })}
              onInput={(v: string) => {
                setReturnError(null);
                props.onSetWhen({ returnDraft: { day: v, time: shownReturnLeg(job).time } });
              }}
            />
            <FormField
              id="quote-return-time"
              label="Return time"
              type="time"
              value={shownReturnLeg(job).time}
              {...(customerReturnHint(job) === null ? {} : { hint: customerReturnHint(job) as string })}
              {...(returnError === null ? {} : { describedBy: [errorIdFor('quote-return-date')] })}
              onInput={(v: string) => {
                setReturnError(null);
                props.onSetWhen({ returnDraft: { day: job.returnDraft?.day ?? null, time: v } });
              }}
            />
          </>
        ) : undefined}
        // Keyed on the SERVICE, not on whether the engine happened to price this
        // job. An airport lead with no rate on file is hand-priced, but the
        // customer still asked for meet & greet and it still persists to the
        // booking row — keying on `handPriced` would drop the very toggle this
        // milestone exists to restore, on exactly the leads that need it most.
        includePricedUpsell={!props.isManual}
      />

      {/* T409 park — quiet, below everything captured, never competing with
          the primary in the bottom bar. One button: the affordance budget on
          this screen is real and shared.
          ⛔ BOARD 938 — "real and shared" understated it: this screen is the
          kiosk's DEEPEST breach of DESIGN.md §1 rule 2, measured at 21 controls
          against a budget of 7 (walk-in fixture: Back, five capture fields plus
          the email disclosure, four band buttons, an exact-passengers number,
          five add-on switches, Change, Save for later, Override, Accept).
          Recorded as tracked debt in test/client/control-budget.spec.tsx
          (T938 R2 (b)); Alex ruled the screen is NOT to be redesigned for it.
          Keeping this one button quiet is still right — just do not read this
          comment as a claim that the screen is inside its budget. */}
      {/* ⭐ T1749 M8 (R7): when a bar renders (`hasBar`), `Save for later` moves
          into the bar's QUIET row (above the fixed bar, beside `Override price`).
          This standalone action-row is only the no-bar case — a parkable job with
          no price yet — where there is no bar to host it. The button and its
          net-locked label are identical either way. */}
      {canPark && !hasBar && (
        <div class="action-row">
          <button
            type="button"
            class="btn btn--quiet"
            disabled={busy}
            onClick={() => void park()}
          >
            Save for later
          </button>
        </div>
      )}

      {/* Manual branch (M5): hand price. */}
      {handPriced && (
        <>
          <div class="hand-price">
            {/* ⭐⭐ T1749 M8 round 2 (code-review + plan-alignment) — THE FOUR
                HAND-ROLLED FIELDS ON THIS SCREEN ADOPT `FormField`. Each was the
                same pair the library composes — a `.field-label` with a `for`, an
                `<input class="login-input">` with the matching id — so the markup
                is unchanged and every `getByLabelText` still resolves; what the
                screen stops owning is the WIRING (label ↔ control ↔ hint ↔
                error), which is the half that drifts.
                ⛔ IDS ARE THIS SCREEN'S OWN LITERALS, never wire data: four fixed
                strings for four fields that exist at most once each, which is
                what the per-screen duplicate-id arm in `quote-flow.spec.tsx`
                grades. ⚠️ THE EMPHASIS SIZE IS A LIBRARY MODIFIER (`figure`), not a
                rule of this screen's: §3.2 scopes 24px/600 to a figure the
                operator scans, this is the one field in the tool that holds one,
                and round 3's first cut keyed it on the field's ID — the kiosk
                sheet's only id selector, which round 4 replaced with the modifier
                `FormField` composes on the control itself. */}
            <FormField
              id="agreed-price"
              label="Enter agreed price (€)"
              type="number"
              inputMode="decimal"
              figure
              value={agreedEuros}
              onInput={setAgreedEuros}
            />
            <FormField
              id="agreed-note"
              label="Note (optional)"
              value={agreedNote}
              onInput={setAgreedNote}
            />
          </div>
          {/* §8: the primary lives in the fixed bottom bar — the hand-quote
              surface is tall (capture + venue + requirements), and scroll
              must never be how Helen FINDS the record action (M5 ux-review). */}
          <TotalBar
            /* ⛔⛔ THE SCREENSHOT CHANNEL IS A RECORDED RESIDUAL, NOT A FIX, AND
               THIS BAR WIDENS IT BY ONE CARRIER (T1749 M8 round 2; the same
               sentence `admin/PriceList.tsx` carries for its own).
               `data-fb-opaque` is honoured ONLY by the click recorder; the
               capture path blanks the VALUE of cloned inputs and reproduces
               rendered TEXT verbatim — and R7 put the customer's NAME and PHONE
               into this fixed bar, which is on screen for the whole quoting
               journey, so they DO reach the JPEG on a build with the widget
               enabled. Before R7 the same two facts were on the customer CARD
               only; the bar is a second, always-visible carrier of them.
               What bounds it: the widget is build-gated (a code property, not a
               deployment guarantee). What would close it is extending the capture
               filter under `src/client/feedback`, which is generated from the
               copy under `site/` — the barrier is this milestone's file-ownership
               boundary plus `sync:feedback:check`.
               ⚠️ AND THE CONSENT ITEM WIDENS WITH IT: the open `PENDING - ALEX`
               ask for "an explicit nod that kiosk screenshots may carry customer
               PII" was raised against synthetic fixtures, and each milestone since
               has changed its subject. It must not be closed against the old one. */
            /* ⭐ T1749 M8 (R7) — BAR 1, the `quote` variant: the journey line
               (customer name + phone) and `Save for later` quiet ABOVE the fixed
               figure/primary row. The hand-quote surface carries no `Override`:
               it IS the manual price, so there is no computed price to override. */
            variant="quote"
            label="Agreed"
            totalCents={agreedCents}
            pending={props.quotePending}
            customerName={name}
            customerPhone={phone}
            quiet={canPark ? [{ label: 'Save for later', onClick: () => void park(), disabled: busy }] : []}
            /* K5-4: the locked name at rest, byte-identical (it is pinned in
               e2e and in vitest); the in-flight word ONLY while this screen's
               own record commit is in the air. */
            primaryLabel={committing === 'record' ? 'Recording…' : 'Record agreed price'}
            primaryDisabled={busy || props.quotePending}
            onPrimary={() => void recordAgreed()}
          />
        </>
      )}

      {quote !== null && !handPriced && (
        <>
          {/* §8: Accept + the price it commits, pinned to the bottom bar so a
              tall edit surface can never scroll the primary — or the number
              Helen reads aloud — off-screen. Null while a re-price is in flight
              (K5-2: the bar then shows its label and NO figure at all; the
              headline says "Updating…").

              T549 M4: the label carries the engine's provenance, same word the
              Addons bar already uses. The headline above chips 'Estimate', but
              the headline scrolls and THIS bar does not — so on a tall quote the
              only number on screen when Helen taps Accept was labelled 'Total'
              whether it was a list price or a distance guess.

              ⭐ T1749 M8 (R7) — BAR 1, the `quote` variant: the journey line
              (customer name + phone) and the quiet row of `Save for later` /
              `Override price` ABOVE the figure/primary row. `Override price` was
              its own action-row; it now lives in the bar's quiet slot and drops
              out while its panel is open (`!overrideOpen`), exactly as before.
              K5-3 (QUIET, not secondary) is now structural: a `BarQuietControl`
              is `quiet` by construction, so the escape hatch can never read as
              loud as the gold `Accept`. `Save override` (in the panel below)
              stays secondary — the demotion is of the DOOR, not the commit. */}
          <TotalBar
            /* ⛔⛔ THE SCREENSHOT CHANNEL IS A RECORDED RESIDUAL, NOT A FIX, AND
               THIS BAR WIDENS IT BY ONE CARRIER (T1749 M8 round 2; the same
               sentence `admin/PriceList.tsx` carries for its own).
               `data-fb-opaque` is honoured ONLY by the click recorder; the
               capture path blanks the VALUE of cloned inputs and reproduces
               rendered TEXT verbatim — and R7 put the customer's NAME and PHONE
               into this fixed bar, which is on screen for the whole quoting
               journey, so they DO reach the JPEG on a build with the widget
               enabled. Before R7 the same two facts were on the customer CARD
               only; the bar is a second, always-visible carrier of them.
               What bounds it: the widget is build-gated (a code property, not a
               deployment guarantee). What would close it is extending the capture
               filter under `src/client/feedback`, which is generated from the
               copy under `site/` — the barrier is this milestone's file-ownership
               boundary plus `sync:feedback:check`.
               ⚠️ AND THE CONSENT ITEM WIDENS WITH IT: the open `PENDING - ALEX`
               ask for "an explicit nod that kiosk screenshots may carry customer
               PII" was raised against synthetic fixtures, and each milestone since
               has changed its subject. It must not be closed against the old one. */
            variant="quote"
            label={quote.status === 'estimated' ? 'Est.' : 'Total'}
            totalCents={props.quotePending ? null : quote.totalCents}
            pending={props.quotePending}
            customerName={name}
            customerPhone={phone}
            quiet={[
              ...(canPark
                ? [{ label: 'Save for later', onClick: () => void park(), disabled: busy || props.quotePending }]
                : []),
              ...(!overrideOpen
                ? [{
                    label: 'Override price',
                    // re-arm: reopening the panel must not carry a stale bypass (ux-review)
                    onClick: () => { setOverrideOpen(true); setAmountWarned(false); },
                    disabled: busy || props.quotePending,
                  }]
                : []),
            ]}
            /* K5-4: `Accept` is net-locked at eight e2e sites, so the at-rest
               label is byte-identical; the in-flight word replaces it only
               while this screen's own accept commit is in the air. */
            primaryLabel={committing === 'accept' ? 'Accepting…' : 'Accept'}
            primaryDisabled={busy || props.quotePending}
            onPrimary={() => void accept()}
          />
        </>
      )}

      {overrideOpen && (
        <div class="override-panel">
          {/* The panel's own copy of the failure, and it takes focus by the same
              mechanism — `saveOverride`'s four guards are as invisible from the
              bottom of a tall quote as `recordAgreed`'s was. */}
          {failure && (
            <Banner kind="error" id="override-failure" elementRef={overrideFailureRef} tabIndex={-1}>
              {failure.text}
            </Banner>
          )}
          {/* `describedBy` is the library's own prop for exactly this: the panel's
              failure banner describes both fields, and it is appended after the
              field's own error and hint slots rather than replacing them. */}
          <FormField
            id="override-amount"
            label="Override amount (€)"
            type="number"
            inputMode="decimal"
            value={overrideEuros}
            describedBy={failure ? ['override-failure'] : undefined}
            onInput={(v) => {
              setOverrideEuros(v);
              setAmountWarned(false); // re-arm: a freshly-typed amount can warn again
            }}
          />
          <FormField
            id="override-note"
            label="Reason for override"
            value={overrideNote}
            describedBy={failure ? ['override-failure'] : undefined}
            onInput={setOverrideNote}
          />
          <div class="action-row">
            <button
              type="button"
              class="btn btn--secondary"
              disabled={busy || props.quotePending}
              onClick={() => void saveOverride()}
            >
              Save override
            </button>
            <button type="button" class="btn btn--quiet" disabled={busy} onClick={() => setOverrideOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* D17b: the internal alert lives OFF the action row, as its own quiet
          section below everything — visible once the job has a price. */}
      {/* ⭐ board 2814 M7 (DD-7): never on a run passed to a taxi firm; the server
          cannot gate `/api/notify/send`, which takes a message body only. */}
      {teamAlertEnabled && job.taxiPassed !== true
        && (quote?.totalCents != null || job.finalTotalCents != null || agreedCents != null) && (
        <TeamAlert
          onPreview={() => props.onPreviewAlert(name.trim(), phone.trim())}
          onSendAlert={props.onSendAlert}
        />
      )}
    </div>
  );
}
